import { useEffect, useMemo, useRef, Dispatch, SetStateAction } from 'react';
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import { CpsField, CpsFieldForView, CpsSearchQueryRequest, DEFAULT_ROWS_PER_PAGE } from '@cs-cms/types';
import { useSearchContext } from '@cs-cms/state-management';
import {
  applyGridFiltersToQueryRequest,
  buildInitialCpsSearchQueryRequest
} from '../helpers/cpsGridQueryRequestBuilders';
import { exceedsMaxSearchSkip } from '../helpers/cpsGridSkipLimit';

interface UseCpsGridQueryEffectsParams {
  columns: CpsFieldForView[] | undefined;
  selectedView: number | undefined;
  selectedIndexId: number | undefined;
  currentViewGlobalFilters: { id: number; facetsCount?: number }[] | undefined;
  allFields: CpsField[] | undefined;
  translate: (key: string) => string;
  isLoading: boolean | undefined;
  totalCountFromData: number | undefined;
}

interface UseCpsGridQueryEffectsResult {
  queryRequest: CpsSearchQueryRequest | undefined;
  setQueryRequest: Dispatch<SetStateAction<CpsSearchQueryRequest | undefined>> | undefined;
  compositeFilterDescriptor: CompositeFilterDescriptor | undefined;
  setCompositeFilterDescriptor: ((descriptor: CompositeFilterDescriptor) => void) | undefined;
  sort: SortDescriptor[] | undefined;
  sortChange: (descriptors: SortDescriptor[]) => void;
  pageChange: (event: GridPageChangeEvent) => void;
  pageSizeValue: number;
  rowsCount: number;
}

export const useCpsGridQueryEffects = (params: UseCpsGridQueryEffectsParams): UseCpsGridQueryEffectsResult => {
  const {
    columns,
    selectedView,
    selectedIndexId,
    currentViewGlobalFilters,
    allFields,
    translate,
    isLoading,
    totalCountFromData
  } = params;

  const { queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor } = useSearchContext();

  const initialQueryRequest = useMemo(() => {
    if (!columns || selectedView == null || selectedIndexId == null) {
      return undefined;
    }

    return buildInitialCpsSearchQueryRequest({
      columns,
      selectedView,
      selectedIndexId,
      currentViewGlobalFilters,
      allFields
    });
  }, [columns, selectedView, selectedIndexId, currentViewGlobalFilters, allFields]);

  const queryRequestFromGridFilters = useMemo(() => {
    if (!columns || !queryRequest || selectedView == null || queryRequest.viewId !== selectedView) {
      return null;
    }

    return applyGridFiltersToQueryRequest(queryRequest, compositeFilterDescriptor, columns, translate);
  }, [compositeFilterDescriptor, columns, queryRequest, selectedView, translate]);

  useEffect(() => {
    if (!setQueryRequest || !initialQueryRequest) {
      return;
    }

    const isQueryInSync = queryRequest?.viewId === selectedView && queryRequest?.indexId === selectedIndexId;

    if (isQueryInSync) {
      return;
    }

    setQueryRequest(initialQueryRequest);
  }, [
    initialQueryRequest,
    queryRequest?.viewId,
    queryRequest?.indexId,
    selectedView,
    selectedIndexId,
    setQueryRequest
  ]);

  useEffect(() => {
    if (!setQueryRequest || !queryRequestFromGridFilters) {
      return;
    }

    setQueryRequest(queryRequestFromGridFilters);
  }, [queryRequestFromGridFilters, setQueryRequest]);

  const sort: SortDescriptor[] | undefined = queryRequest?.orderBy
    ? [
        {
          field: queryRequest.orderBy,
          dir: queryRequest.orderByIsDescending ? 'desc' : 'asc'
        }
      ]
    : undefined;

  const sortChange = (descriptors: SortDescriptor[]): void => {
    if (!queryRequest || !setQueryRequest) {
      return;
    }

    setQueryRequest({
      ...queryRequest,
      orderBy: descriptors.length > 0 ? descriptors[0].field : undefined,
      orderByIsDescending: descriptors.length > 0 && descriptors[0].dir === 'desc' ? true : undefined
    });
  };

  const pageSizeValue = queryRequest?.size ?? DEFAULT_ROWS_PER_PAGE;

  const lastKnownTotalCountRef = useRef<number>(0);
  const totalCount = totalCountFromData ?? 0;
  const isPlaceholderTotalCount = totalCount === pageSizeValue && lastKnownTotalCountRef.current > pageSizeValue;
  const isZeroTotalWhileLoading = Boolean(isLoading) && totalCount === 0 && lastKnownTotalCountRef.current > 0;

  if (totalCount > 0 && !isPlaceholderTotalCount) {
    lastKnownTotalCountRef.current = totalCount;
  }

  const rowsCount =
    (isLoading && isPlaceholderTotalCount) || isZeroTotalWhileLoading ? lastKnownTotalCountRef.current : totalCount;

  const pageChange = (event: GridPageChangeEvent): void => {
    if (!queryRequest || !setQueryRequest) {
      return;
    }

    const { skip, take } = event.page;
    if (exceedsMaxSearchSkip(skip)) {
      return;
    }

    setQueryRequest({
      ...queryRequest,
      skip,
      size: take
    });
  };

  return {
    queryRequest,
    setQueryRequest,
    compositeFilterDescriptor,
    setCompositeFilterDescriptor,
    sort,
    sortChange,
    pageChange,
    pageSizeValue,
    rowsCount
  };
};
